import React, { useCallback, useContext } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./kyb.css";
import { ProfileContext } from "../../../context/ProfileProvider";
import { useKybStatusRouting } from "./useKybStatus";
import { useKybVerifyAction } from "./kybCtaHelpers";
import { pickKybApplicationId, pickKybBusinessName } from "./kybDisplayFields";

export default function KYBPending() {
  const profileCtx = useContext(ProfileContext) || {};
  const handleUserDetails = profileCtx.handleUserDetails;

  const {
    kybStatusRes,
    canonicalStatus,
    diditStatus,
    sessionPayload,
    hasResponse,
    loadKybStatus,
  } = useKybStatusRouting();

  const onFlowEnd = useCallback(() => {
    void loadKybStatus();
    handleUserDetails?.();
  }, [loadKybStatus, handleUserDetails]);

  const { busy, cta, startVerify } = useKybVerifyAction({
    kybStatusRes,
    canonicalStatus,
    diditStatus,
    onFlowEnd,
  });

  const inProgress = diditStatus === "In Progress";
  const statusLabel = inProgress ? "In Progress" : (diditStatus || "Pending");
  const businessName = pickKybBusinessName(sessionPayload);
  const applicationId = pickKybApplicationId(sessionPayload);

  return (
    <div className="dashboard_right">
      <Helmet>
        <meta
          httpEquiv="Permissions-Policy"
          content="camera=*, microphone=*, display-capture=*, fullscreen=*, publickey-credentials-get=*"
        />
      </Helmet>
      <div className="kyc-page kyb_verification_page">

        <div className="kyb_page_header">
          <h1>KYB Verification</h1>
        </div>
        <div className="kyb_status_container pending_section">
          <div className="kyb_status_icon_wrap">
            <img
              src={inProgress
                ? "/images/kyb-verification-img/progress_vector.svg"
                : "/images/kyb-verification-img/kyb_pending_vector.svg"}
              alt={inProgress ? "In Progress" : "Pending"}
            />
          </div>

          <h2 className="kyb_status_title">
            KYB Verification{" "}
            <span className="pending">{inProgress ? "In Progress" : "Pending"}</span>
          </h2>

          <p className="kyb_status_desc">
            {inProgress
              ? "Your KYB verification session is still open. Continue to complete the remaining steps."
              : (
                <>
                  Your business verification is currently under review.<br />
                  We will notify you once it is approved or if any additional information is required.
                </>
              )}
            {diditStatus ? (
              <>
                <br />
                <span className="kyb_status_vendor">Status: {diditStatus}</span>
              </>
            ) : null}
          </p>

          {/* Same page: Continue when In Progress; no button while In Review */}
          {cta.show ? (
            <button
              type="button"
              className={inProgress ? "kyb_continue_btn" : "kyb_start_btn kyb_status_cta"}
              onClick={() => void startVerify()}
              disabled={busy || !hasResponse}
              aria-busy={busy}
            >
              {busy ? "Starting…" : cta.label}
            </button>
          ) : null}

          <div className="kyb_info_card">

            <div className="kyb_info_item">
              <div className="kyb_info_icon">
                <img src="/images/kyb-verification-img/kyb_pending_icon.svg" alt="Icon" />
              </div>
              <div className="kyb_info_text">
                <div className="kyb_info_label">Business Name</div>
                <div className="kyb_info_value">{businessName}</div>
              </div>
            </div>

            <div className="kyb_info_item">
              <div className="kyb_info_icon">
                <img src="/images/kyb-verification-img/kyb_pending_icon3.svg" alt="Icon" />
              </div>
              <div className="kyb_info_text">
                <div className="kyb_info_label">Application ID</div>
                <div className="kyb_info_value">{applicationId}</div>
              </div>
            </div>

            <div className="kyb_info_item">
              <div className="kyb_info_icon">
                <img src="/images/kyb-verification-img/kyb_pending_icon4.svg" alt="Icon" />
              </div>
              <div className="kyb_info_text">
                <div className="kyb_info_label">Verification Status</div>
                <div className="kyb_info_value pending_value">
                  <i className="ri-time-line"></i> {statusLabel}
                </div>
              </div>
            </div>

            <div className="kyb_info_item">
              <div className="kyb_info_icon">
                <img src="/images/kyb-verification-img/kyb_pending_icon5.svg" alt="Icon" />
              </div>
              <div className="kyb_info_text">
                <div className="kyb_info_label">Verified By</div>
                <div className="kyb_info_value">Arab Global Crypto Exchange</div>
              </div>
            </div>

            <div className="kyb_info_item">
              <div className="kyb_info_icon">
                <img src="/images/kyb-verification-img/kyb_pending_icon6.svg" alt="Icon" />
              </div>
              <div className="kyb_info_text">
                <div className="kyb_info_label">Verification Method</div>
                <div className="kyb_info_value">Business Verification (KYB)</div>
              </div>
            </div>

          </div>

          <div className="kyb_help_box">
            <div className="kyb_help_left">
              <div className="kyb_help_icon">
                <img src="/images/kyb-verification-img/help_icon.svg" alt="Icon" />
              </div>
              <div className="kyb_help_text">
                <h4>Need Help?</h4>
                <p>If you have any questions or need assistance, our support team is here to help.</p>
              </div>
            </div>
            <Link to="/user_profile/my_tickets" className="kyb_help_btn">
              Submit a Ticket <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

        </div>

      </div>
    </div>
  );
}
